import {
  hatAboutItems,
  hatColorOptions,
  hatMoreDetails,
  hatPerspectiveItems,
  hatProductDetails,
  hatPromotionItems,
  hatQualityItems,
  hatSizeOptions,
} from "./apparelHatContent";
import type { HatColorOption, HatDetailRow, HatPerspectiveItem, HatQualityItem } from "./apparelHatContent";

export type OneStopProductKind = "apparel" | "gift" | "equipment";

export interface OneStopPromotionItem {
  status: string;
  label: string;
  description: string;
}

export interface OneStopProductContent {
  kind: OneStopProductKind;
  slug: string;
  title: string;
  categoryLabel: string;
  productName: string;
  heroImage: string;
  credit: string;
  price: string;
  stockLevel: string;
  optionLabel: string;
  colorSectionTitle: string;
  colorOptions: HatColorOption[];
  productDetails: HatDetailRow[];
  moreDetails: HatDetailRow[];
  aboutItems: string[];
  sizeOptions: string[];
  promotionItems: OneStopPromotionItem[];
  perspectiveItems: HatPerspectiveItem[];
  qualityItems: HatQualityItem[];
  description: string;
}

interface OneStopProductSource {
  kind: OneStopProductKind;
  title: string;
  productName: string;
  heroImage: string;
  perspectiveImages?: {
    side: string;
    top: string;
  };
  credit?: string;
  price?: string;
  stockLevel?: string;
  optionLabel?: string;
  colorSectionTitle?: string;
  colors: Array<Pick<HatColorOption, "id" | "label" | "swatchColor" | "textColor">>;
  productDetails: HatDetailRow[];
  moreDetails: HatDetailRow[];
  aboutItems: string[];
  sizeOptions: string[];
  description: string;
  qualityItems: Array<Pick<HatQualityItem, "title" | "description">>;
}

const categoryLabels: Record<OneStopProductKind, string> = {
  apparel: "Apparel",
  gift: "Gift",
  equipment: "Equipment",
};

export const normalizeOneStopSlug = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const makeColorVariantImage = (kind: OneStopProductKind, slug: string, colorId: string) =>
  `/image/one-stop/${kind}/colors/${slug}-${colorId}.webp`;

const makeColorOptions = (
  kind: OneStopProductKind,
  slug: string,
  colors: Array<Pick<HatColorOption, "id" | "label" | "swatchColor" | "textColor">>,
): HatColorOption[] =>
  colors.map((color) => ({
    ...color,
    image: makeColorVariantImage(kind, slug, color.id),
  }));

const makePerspectiveItems = (
  image: string,
  perspectiveImages?: OneStopProductSource["perspectiveImages"],
): HatPerspectiveItem[] => [
  {
    title: "Front view",
    image,
    className: "front",
  },
  {
    title: "Side view",
    image: perspectiveImages?.side ?? image,
    className: "side",
  },
  {
    title: "Top view",
    image: perspectiveImages?.top ?? image,
    className: "top",
  },
];

const makeQualityItems = (
  image: string,
  items: Array<Pick<HatQualityItem, "title" | "description">>,
  perspectiveImages?: OneStopProductSource["perspectiveImages"],
): HatQualityItem[] =>
  items.map((item, index) => ({
    ...item,
    image: [image, perspectiveImages?.side ?? image, perspectiveImages?.top ?? image][index % 3],
  }));

const makeOneStopProductImage = (kind: OneStopProductKind, slug: string) => `/image/one-stop/${kind}/${slug}.webp`;

const makeOneStopPerspectiveImageSet = (kind: OneStopProductKind, slug: string) => ({
  side: `/image/one-stop/${kind}/perspective/${slug}-side.webp`,
  top: `/image/one-stop/${kind}/perspective/${slug}-top.webp`,
});

const standardPromotions: OneStopPromotionItem[] = [
  { status: "Redeemed", label: "Savings", description: "Buy 2, save 5%" },
  { status: "Redeemed", label: "Savings", description: "Buy 3, save 10%" },
];

const buildProduct = (source: OneStopProductSource): OneStopProductContent => {
  const slug = normalizeOneStopSlug(source.title);

  return {
    ...source,
    slug,
    categoryLabel: categoryLabels[source.kind],
    credit: source.credit ?? "1000",
    price: source.price ?? "$ 18.88",
    stockLevel: source.stockLevel ?? "> 10",
    optionLabel: source.optionLabel ?? "Size:",
    colorSectionTitle: source.colorSectionTitle ?? "COLOR DISPLAY",
    colorOptions: makeColorOptions(source.kind, slug, source.colors),
    promotionItems: standardPromotions,
    perspectiveItems: makePerspectiveItems(source.heroImage, source.perspectiveImages),
    qualityItems: makeQualityItems(source.heroImage, source.qualityItems, source.perspectiveImages),
  };
};

const apparelProducts: OneStopProductSource[] = [
  {
    kind: "apparel",
    title: "Gloves",
    productName: "Marco Work Gloves",
    heroImage: makeOneStopProductImage("apparel", "gloves"),
    perspectiveImages: makeOneStopPerspectiveImageSet("apparel", "gloves"),
    price: "$ 16.88",
    colors: [
      { id: "black-red", label: "Blue", swatchColor: "#34588a", textColor: "#ffffff" },
      { id: "graphite", label: "Gray", swatchColor: "#6f6f6f", textColor: "#ffffff" },
      { id: "signal-red", label: "Red", swatchColor: "#d9251f", textColor: "#ffffff" },
    ],
    productDetails: [
      { label: "Material composition", value: "Synthetic leather and elastic fabric" },
      { label: "Care instructions", value: "Hand Wash" },
      { label: "Origin", value: "Imported" },
      { label: "Closure type", value: "Elastic cuff" },
    ],
    moreDetails: [
      { label: "Size", value: "M / L / XL" },
      { label: "Date First Available", value: "30 March 2025" },
      { label: "Manufacturer", value: "Marco Apparel" },
      { label: "Marco number", value: "APP-GLV-001" },
      { label: "Department", value: "Unisex-adult" },
    ],
    aboutItems: [
      "Reinforced palm surface for warehouse and workshop handling",
      "Flexible knuckle zones keep the fit comfortable through long shifts",
      "Marco red detail keeps the workwear line visually consistent",
      "Lightweight cuff makes the gloves easy to pull on and remove",
    ],
    sizeOptions: ["M", "L", "XL"],
    description:
      "A durable pair of Marco work gloves for daily warehouse, shop, and outdoor tasks. The reinforced grip area protects the palm while the flexible back panel keeps movement easy.",
    qualityItems: [
      { title: "Reinforced palm", description: "Extra grip surface supports repeated handling and carrying" },
      { title: "Elastic wrist", description: "The cuff keeps the glove stable without feeling bulky" },
      { title: "Flexible backing", description: "Stretch fabric helps the glove move naturally with the hand" },
      { title: "Brand detail", description: "Red Marco accents make the set easy to match with other apparel" },
    ],
  },
  {
    kind: "apparel",
    title: "Mountaineering Clothes",
    productName: "Marco Outdoor Jacket",
    heroImage: makeOneStopProductImage("apparel", "mountaineering-clothes"),
    perspectiveImages: makeOneStopPerspectiveImageSet("apparel", "mountaineering-clothes"),
    price: "$ 42.88",
    colors: [
      { id: "black", label: "Black", swatchColor: "#1e1f20", textColor: "#ffffff" },
      { id: "marco-red", label: "Red", swatchColor: "#d9251f", textColor: "#ffffff" },
      { id: "stone-gray", label: "Gray", swatchColor: "#9d9d9d", textColor: "#231815" },
    ],
    productDetails: [
      { label: "Material composition", value: "Polyester shell with soft lining" },
      { label: "Care instructions", value: "Machine Wash" },
      { label: "Origin", value: "Imported" },
      { label: "Closure type", value: "Zipper" },
    ],
    moreDetails: [
      { label: "Size", value: "S / M / L / XL / XXL" },
      { label: "Date First Available", value: "30 March 2025" },
      { label: "Manufacturer", value: "Marco Apparel" },
      { label: "Marco number", value: "APP-JKT-001" },
      { label: "Department", value: "Unisex-adult" },
    ],
    aboutItems: [
      "Outdoor shell for travel, team events, and daily wear",
      "Clean Marco identity with high-contrast trim",
      "Layer-friendly fit works over T-shirts or hoodies",
      "Durable zipper and pocket details support frequent use",
    ],
    sizeOptions: ["S", "M", "L", "XL", "XXL"],
    description:
      "A practical Marco outdoor jacket designed for branded team wear. It is light enough for travel and structured enough for events, workdays, and customer visits.",
    qualityItems: [
      { title: "Weather-ready shell", description: "Smooth polyester surface helps block light wind" },
      { title: "Full zipper", description: "Easy open-close construction for quick temperature changes" },
      { title: "Secure pockets", description: "Pocket placement keeps daily essentials close" },
      { title: "Layered fit", description: "Roomy cut works comfortably over base layers" },
    ],
  },
  {
    kind: "apparel",
    title: "Scarf",
    productName: "Marco Knit Scarf",
    heroImage: makeOneStopProductImage("apparel", "scarf"),
    perspectiveImages: makeOneStopPerspectiveImageSet("apparel", "scarf"),
    price: "$ 12.88",
    colors: [
      { id: "black", label: "Black", swatchColor: "#1e1f20", textColor: "#ffffff" },
      { id: "red", label: "Red", swatchColor: "#d9251f", textColor: "#ffffff" },
      { id: "gray", label: "Gray", swatchColor: "#c8c8c8", textColor: "#231815" },
    ],
    productDetails: [
      { label: "Material composition", value: "Acrylic knit" },
      { label: "Care instructions", value: "Hand Wash" },
      { label: "Origin", value: "Imported" },
      { label: "Closure type", value: "Pull On" },
    ],
    moreDetails: [
      { label: "Size", value: "One size" },
      { label: "Date First Available", value: "30 March 2025" },
      { label: "Manufacturer", value: "Marco Apparel" },
      { label: "Marco number", value: "APP-SCF-001" },
      { label: "Department", value: "Unisex-adult" },
    ],
    aboutItems: [
      "Soft knit texture for seasonal team apparel",
      "Long shape allows simple wrap styling",
      "Works with jackets, hoodies, and event uniforms",
      "Compact gift item for customer visits or staff rewards",
    ],
    sizeOptions: ["One size"],
    description:
      "A soft Marco scarf for seasonal apparel programs and easy gifting. The knit body is warm, flexible, and simple to pair with the rest of the Marco apparel line.",
    qualityItems: [
      { title: "Soft knit", description: "Comfortable yarn creates a warm hand feel" },
      { title: "Clean edges", description: "Finished edges help the scarf keep its shape" },
      { title: "Easy styling", description: "Long profile supports multiple wrap styles" },
      { title: "Gift ready", description: "Lightweight format works well for events and rewards" },
    ],
  },
  {
    kind: "apparel",
    title: "Socks",
    productName: "Marco Crew Socks",
    heroImage: makeOneStopProductImage("apparel", "socks"),
    perspectiveImages: makeOneStopPerspectiveImageSet("apparel", "socks"),
    price: "$ 8.88",
    colors: [
      { id: "black", label: "Black", swatchColor: "#1e1f20", textColor: "#ffffff" },
      { id: "white", label: "White", swatchColor: "#e8e9e8", textColor: "#231815" },
      { id: "red-trim", label: "Red", swatchColor: "#d9251f", textColor: "#ffffff" },
    ],
    productDetails: [
      { label: "Material composition", value: "Cotton blend" },
      { label: "Care instructions", value: "Machine Wash" },
      { label: "Origin", value: "Imported" },
      { label: "Closure type", value: "Pull On" },
    ],
    moreDetails: [
      { label: "Size", value: "EU 39-44" },
      { label: "Date First Available", value: "30 March 2025" },
      { label: "Manufacturer", value: "Marco Apparel" },
      { label: "Marco number", value: "APP-SCK-001" },
      { label: "Department", value: "Unisex-adult" },
    ],
    aboutItems: [
      "Cotton-blend fabric keeps the socks breathable",
      "Crew height works well with boots and casual shoes",
      "Elastic cuff keeps the sock in place",
      "Small Marco detail makes it an easy branded giveaway",
    ],
    sizeOptions: ["39-44"],
    description:
      "Comfortable Marco crew socks for daily wear, travel kits, and customer gifts. The cotton-blend body balances softness, stretch, and easy care.",
    qualityItems: [
      { title: "Elastic cuff", description: "Stable ribbing helps the sock stay in place" },
      { title: "Breathable blend", description: "Cotton-rich fabric supports daily comfort" },
      { title: "Crew profile", description: "Versatile height pairs with boots or casual shoes" },
      { title: "Soft toe area", description: "Smooth toe construction reduces irritation" },
    ],
  },
  {
    kind: "apparel",
    title: "T-Shirt",
    productName: "Marco Cotton T-Shirt",
    heroImage: makeOneStopProductImage("apparel", "t-shirt"),
    perspectiveImages: makeOneStopPerspectiveImageSet("apparel", "t-shirt"),
    price: "$ 18.88",
    colors: [
      { id: "black", label: "Black", swatchColor: "#1e1f20", textColor: "#ffffff" },
      { id: "white", label: "White", swatchColor: "#e8e9e8", textColor: "#231815" },
      { id: "red", label: "Red", swatchColor: "#d9251f", textColor: "#ffffff" },
    ],
    productDetails: [
      { label: "Material composition", value: "100% Cotton" },
      { label: "Care instructions", value: "Machine Wash" },
      { label: "Origin", value: "Imported" },
      { label: "Closure type", value: "Pull On" },
    ],
    moreDetails: [
      { label: "Size", value: "S / M / L / XL / XXL" },
      { label: "Date First Available", value: "30 March 2025" },
      { label: "Manufacturer", value: "Marco Apparel" },
      { label: "Marco number", value: "APP-TEE-001" },
      { label: "Department", value: "Unisex-adult" },
    ],
    aboutItems: [
      "Clean everyday tee for staff, events, and customer gifts",
      "Soft cotton construction keeps the fit breathable",
      "Marco branding is simple and easy to coordinate",
      "Works alone or layered under jackets and hoodies",
    ],
    sizeOptions: ["S", "M", "L", "XL", "XXL"],
    description:
      "A simple Marco cotton T-shirt for everyday use. It is designed as a reliable apparel staple for events, teams, travel, and customer reward programs.",
    qualityItems: [
      { title: "Soft cotton", description: "Breathable fabric supports everyday wear" },
      { title: "Classic collar", description: "Crew neck shape keeps the profile clean" },
      { title: "Straight hem", description: "Simple hem works tucked or untucked" },
      { title: "Easy care", description: "Machine-washable construction is simple to maintain" },
    ],
  },
  {
    kind: "apparel",
    title: "Tie",
    productName: "Marco Business Tie",
    heroImage: makeOneStopProductImage("apparel", "tie"),
    perspectiveImages: makeOneStopPerspectiveImageSet("apparel", "tie"),
    price: "$ 14.88",
    colors: [
      { id: "black", label: "Black", swatchColor: "#1e1f20", textColor: "#ffffff" },
      { id: "red", label: "Red", swatchColor: "#d9251f", textColor: "#ffffff" },
      { id: "navy", label: "Navy", swatchColor: "#1f3a5f", textColor: "#ffffff" },
    ],
    productDetails: [
      { label: "Material composition", value: "Polyester silk feel" },
      { label: "Care instructions", value: "Dry Clean" },
      { label: "Origin", value: "Imported" },
      { label: "Closure type", value: "Self tie" },
    ],
    moreDetails: [
      { label: "Size", value: "One size" },
      { label: "Date First Available", value: "30 March 2025" },
      { label: "Manufacturer", value: "Marco Apparel" },
      { label: "Marco number", value: "APP-TIE-001" },
      { label: "Department", value: "Unisex-adult" },
    ],
    aboutItems: [
      "Business-ready accessory for dealer meetings and events",
      "Smooth fabric has a polished, professional finish",
      "Easy to pair with white, black, or grey shirts",
      "Lightweight gift format for conference and sales teams",
    ],
    sizeOptions: ["One size"],
    description:
      "A polished Marco tie for meetings, exhibitions, and branded business dress. The smooth fabric adds a refined finish without becoming difficult to care for.",
    qualityItems: [
      { title: "Smooth surface", description: "Silk-feel fabric keeps the tie formal and sharp" },
      { title: "Balanced width", description: "Classic profile works with most shirt collars" },
      { title: "Clean drape", description: "Light structure helps the tie sit neatly" },
      { title: "Event ready", description: "Compact accessory suits sales and exhibition kits" },
    ],
  },
  {
    kind: "apparel",
    title: "Work Clothes",
    productName: "Marco Workwear Set",
    heroImage: makeOneStopProductImage("apparel", "work-clothes"),
    perspectiveImages: makeOneStopPerspectiveImageSet("apparel", "work-clothes"),
    price: "$ 48.88",
    colors: [
      { id: "graphite", label: "Gray", swatchColor: "#6f6f6f", textColor: "#ffffff" },
      { id: "black", label: "Black", swatchColor: "#1e1f20", textColor: "#ffffff" },
      { id: "red-trim", label: "Red", swatchColor: "#d9251f", textColor: "#ffffff" },
    ],
    productDetails: [
      { label: "Material composition", value: "Cotton polyester twill" },
      { label: "Care instructions", value: "Machine Wash" },
      { label: "Origin", value: "Imported" },
      { label: "Closure type", value: "Button and zipper" },
    ],
    moreDetails: [
      { label: "Size", value: "M / L / XL / XXL" },
      { label: "Date First Available", value: "30 March 2025" },
      { label: "Manufacturer", value: "Marco Apparel" },
      { label: "Marco number", value: "APP-WRK-001" },
      { label: "Department", value: "Unisex-adult" },
    ],
    aboutItems: [
      "Durable workwear for warehouse, service, and installation teams",
      "Utility pockets support tools, pens, and daily items",
      "Structured twill fabric balances toughness and comfort",
      "Marco trim keeps field teams aligned with brand identity",
    ],
    sizeOptions: ["M", "L", "XL", "XXL"],
    description:
      "Marco work clothes built for daily operational teams. The set uses a tough twill fabric, practical pocket layout, and brand details that look consistent across a crew.",
    qualityItems: [
      { title: "Durable twill", description: "Fabric is selected for frequent workday use" },
      { title: "Utility pocketing", description: "Pockets are placed for tools and daily carry" },
      { title: "Reinforced seams", description: "Stress points are designed for movement" },
      { title: "Team identity", description: "Marco details create a unified field look" },
    ],
  },
  {
    kind: "apparel",
    title: "Work Shoes",
    productName: "Marco Work Shoes",
    heroImage: makeOneStopProductImage("apparel", "work-shoes"),
    perspectiveImages: makeOneStopPerspectiveImageSet("apparel", "work-shoes"),
    price: "$ 38.88",
    colors: [
      { id: "black", label: "Black", swatchColor: "#1e1f20", textColor: "#ffffff" },
      { id: "gray", label: "Gray", swatchColor: "#6f6f6f", textColor: "#ffffff" },
      { id: "red-accent", label: "Red", swatchColor: "#d9251f", textColor: "#ffffff" },
    ],
    productDetails: [
      { label: "Material composition", value: "Synthetic upper and rubber outsole" },
      { label: "Care instructions", value: "Wipe Clean" },
      { label: "Origin", value: "Imported" },
      { label: "Closure type", value: "Lace-up" },
    ],
    moreDetails: [
      { label: "Size", value: "EU 39-45" },
      { label: "Date First Available", value: "30 March 2025" },
      { label: "Manufacturer", value: "Marco Apparel" },
      { label: "Marco number", value: "APP-SHO-001" },
      { label: "Department", value: "Unisex-adult" },
    ],
    aboutItems: [
      "Stable outsole for shop floors and warehouse movement",
      "Cushioned collar improves comfort during long days",
      "Lace-up structure allows an adjustable secure fit",
      "Marco accent details pair with the workwear line",
    ],
    sizeOptions: ["39", "40", "41", "42", "43", "44", "45"],
    description:
      "Marco work shoes designed for team uniforms and active workdays. The synthetic upper is easy to maintain, while the outsole gives stable daily support.",
    qualityItems: [
      { title: "Stable outsole", description: "Rubber sole supports grip on common work surfaces" },
      { title: "Padded collar", description: "Soft collar area reduces pressure around the ankle" },
      { title: "Lace-up fit", description: "Adjustable lacing helps secure the foot" },
      { title: "Easy cleaning", description: "Synthetic upper can be wiped down after work" },
    ],
  },
];

const giftProducts: OneStopProductSource[] = [
  {
    kind: "gift",
    title: "Tent",
    productName: "Marco Outdoor Tent",
    heroImage: makeOneStopProductImage("gift", "tent"),
    perspectiveImages: makeOneStopPerspectiveImageSet("gift", "tent"),
    price: "$ 58.88",
    optionLabel: "Capacity:",
    colorSectionTitle: "STYLE DISPLAY",
    colors: [
      { id: "gray-red", label: "Blue", swatchColor: "#34588a", textColor: "#ffffff" },
      { id: "black", label: "Dark Gray", swatchColor: "#5c5c5c", textColor: "#ffffff" },
      { id: "field", label: "Olive Green", swatchColor: "#707c58", textColor: "#ffffff" },
    ],
    productDetails: [
      { label: "Material composition", value: "Water-resistant polyester" },
      { label: "Care instructions", value: "Air Dry" },
      { label: "Origin", value: "Imported" },
      { label: "Closure type", value: "Zipper" },
    ],
    moreDetails: [
      { label: "Size", value: "2-person" },
      { label: "Date First Available", value: "30 March 2025" },
      { label: "Manufacturer", value: "Marco Gift" },
      { label: "Marco number", value: "GFT-TNT-001" },
      { label: "Department", value: "Outdoor gift" },
    ],
    aboutItems: [
      "Outdoor gift item for travel, camping, and customer events",
      "Compact pack-down format is easy to store and move",
      "Water-resistant fabric supports light outdoor use",
      "Marco branding turns the tent into a memorable reward item",
    ],
    sizeOptions: ["2-person"],
    description:
      "A Marco-branded outdoor tent for campaigns, loyalty rewards, and event giveaways. It is designed as a practical gift with strong visual presence.",
    qualityItems: [
      { title: "Compact storage", description: "Packable structure makes transport easier" },
      { title: "Zipper opening", description: "Simple entry construction is easy to operate" },
      { title: "Outdoor fabric", description: "Polyester surface supports light outdoor use" },
      { title: "Gift impact", description: "Large branded surface creates strong event visibility" },
    ],
  },
  {
    kind: "gift",
    title: "Coffee Cup",
    productName: "Marco Coffee Cup",
    heroImage: makeOneStopProductImage("gift", "coffee-cup"),
    perspectiveImages: makeOneStopPerspectiveImageSet("gift", "coffee-cup"),
    price: "$ 9.88",
    optionLabel: "Capacity:",
    colorSectionTitle: "STYLE DISPLAY",
    colors: [
      { id: "white", label: "White", swatchColor: "#e8e9e8", textColor: "#231815" },
      { id: "black", label: "Gray", swatchColor: "#6f6f6f", textColor: "#ffffff" },
      { id: "red", label: "Red", swatchColor: "#d9251f", textColor: "#ffffff" },
    ],
    productDetails: [
      { label: "Material composition", value: "Ceramic" },
      { label: "Care instructions", value: "Hand Wash" },
      { label: "Origin", value: "Imported" },
      { label: "Closure type", value: "Open top" },
    ],
    moreDetails: [
      { label: "Capacity", value: "350ML" },
      { label: "Date First Available", value: "30 March 2025" },
      { label: "Manufacturer", value: "Marco Gift" },
      { label: "Marco number", value: "GFT-CFC-001" },
      { label: "Department", value: "Desk gift" },
    ],
    aboutItems: [
      "Everyday desk cup for offices, counters, and customer gifts",
      "Smooth ceramic surface is comfortable to hold",
      "Clean Marco mark keeps the cup professional",
      "Works well in gift boxes, meeting rooms, and showrooms",
    ],
    sizeOptions: ["350ML"],
    description:
      "A classic Marco ceramic coffee cup for daily desk use and simple customer gifting. The clean form keeps the brand visible without feeling busy.",
    qualityItems: [
      { title: "Ceramic body", description: "Smooth ceramic keeps a familiar premium hand feel" },
      { title: "Comfort handle", description: "Handle shape is easy to grip during daily use" },
      { title: "Desk friendly", description: "Stable base works well on office surfaces" },
      { title: "Gift ready", description: "Simple profile fits cleanly in gift packaging" },
    ],
  },
  {
    kind: "gift",
    title: "Crayon",
    productName: "Marco Crayon Set",
    heroImage: makeOneStopProductImage("gift", "crayon"),
    perspectiveImages: makeOneStopPerspectiveImageSet("gift", "crayon"),
    price: "$ 6.88",
    optionLabel: "Set:",
    colorSectionTitle: "STYLE DISPLAY",
    colors: [
      { id: "multi", label: "Yellow", swatchColor: "#d2b248", textColor: "#231815" },
      { id: "red-box", label: "Red", swatchColor: "#d9251f", textColor: "#ffffff" },
      { id: "blue-box", label: "Blue", swatchColor: "#34588a", textColor: "#ffffff" },
    ],
    productDetails: [
      { label: "Material composition", value: "Wax color sticks" },
      { label: "Care instructions", value: "Keep Dry" },
      { label: "Origin", value: "Imported" },
      { label: "Closure type", value: "Box" },
    ],
    moreDetails: [
      { label: "Set", value: "12 colors" },
      { label: "Date First Available", value: "30 March 2025" },
      { label: "Manufacturer", value: "Marco Gift" },
      { label: "Marco number", value: "GFT-CRY-001" },
      { label: "Department", value: "Family gift" },
    ],
    aboutItems: [
      "Small creative gift for family-friendly events",
      "Compact carton is easy to hand out in welcome kits",
      "Bright color set supports simple drawing activities",
      "A softer gift choice for exhibitions and open days",
    ],
    sizeOptions: ["12 colors"],
    description:
      "A compact Marco crayon set for family events, open days, and light creative gifting. It is easy to pack, easy to hand out, and immediately useful.",
    qualityItems: [
      { title: "Bright colors", description: "The set includes a broad range for simple sketching" },
      { title: "Compact box", description: "Carton packaging keeps the crayons organized" },
      { title: "Easy giveaway", description: "Small size works well in event bags" },
      { title: "Family appeal", description: "Useful for customer events with children" },
    ],
  },
  {
    kind: "gift",
    title: "Cup",
    productName: "Marco Travel Cup",
    heroImage: makeOneStopProductImage("gift", "cup"),
    perspectiveImages: makeOneStopPerspectiveImageSet("gift", "cup"),
    price: "$ 12.88",
    optionLabel: "Capacity:",
    colorSectionTitle: "STYLE DISPLAY",
    colors: [
      { id: "black", label: "Black", swatchColor: "#1e1f20", textColor: "#ffffff" },
      { id: "white", label: "White", swatchColor: "#e8e9e8", textColor: "#231815" },
      { id: "red", label: "Red", swatchColor: "#d9251f", textColor: "#ffffff" },
    ],
    productDetails: [
      { label: "Material composition", value: "Stainless steel and PP lid" },
      { label: "Care instructions", value: "Hand Wash" },
      { label: "Origin", value: "Imported" },
      { label: "Closure type", value: "Lid" },
    ],
    moreDetails: [
      { label: "Capacity", value: "500ML" },
      { label: "Date First Available", value: "30 March 2025" },
      { label: "Manufacturer", value: "Marco Gift" },
      { label: "Marco number", value: "GFT-CUP-001" },
      { label: "Department", value: "Travel gift" },
    ],
    aboutItems: [
      "Reusable travel cup for office, car, and event use",
      "Lid helps reduce spills during movement",
      "Marco mark gives the item a clean corporate look",
      "Practical enough for daily customer retention gifts",
    ],
    sizeOptions: ["500ML"],
    description:
      "A reusable Marco travel cup for daily drinks, meetings, and road use. It is a practical gift that keeps the brand close to everyday routines.",
    qualityItems: [
      { title: "Reusable body", description: "Stainless steel construction supports repeated use" },
      { title: "Secure lid", description: "Fitted lid helps reduce accidental spills" },
      { title: "Travel format", description: "Slim shape is easy to carry between locations" },
      { title: "Daily visibility", description: "A practical item that keeps the brand in sight" },
    ],
  },
  {
    kind: "gift",
    title: "Fishing Gear",
    productName: "Marco Fishing Gear Set",
    heroImage: makeOneStopProductImage("gift", "fishing-gear"),
    perspectiveImages: makeOneStopPerspectiveImageSet("gift", "fishing-gear"),
    price: "$ 28.88",
    optionLabel: "Set:",
    colorSectionTitle: "STYLE DISPLAY",
    colors: [
      { id: "black", label: "Black", swatchColor: "#1e1f20", textColor: "#ffffff" },
      { id: "silver", label: "Silver Gray", swatchColor: "#c9c9c9", textColor: "#231815" },
      { id: "red", label: "Red", swatchColor: "#d9251f", textColor: "#ffffff" },
    ],
    productDetails: [
      { label: "Material composition", value: "Mixed fishing accessories" },
      { label: "Care instructions", value: "Rinse and Dry" },
      { label: "Origin", value: "Imported" },
      { label: "Closure type", value: "Carry case" },
    ],
    moreDetails: [
      { label: "Set", value: "Starter kit" },
      { label: "Date First Available", value: "30 March 2025" },
      { label: "Manufacturer", value: "Marco Gift" },
      { label: "Marco number", value: "GFT-FSH-001" },
      { label: "Department", value: "Outdoor gift" },
    ],
    aboutItems: [
      "Outdoor-focused gift kit for leisure and customer reward programs",
      "Organized carry case keeps small accessories together",
      "Compact enough for event gifting and travel",
      "A distinctive alternative to desk-only gifts",
    ],
    sizeOptions: ["Starter kit"],
    description:
      "A compact Marco fishing gear starter set for outdoor-themed campaigns and memorable customer rewards. The carry format keeps the small pieces organized.",
    qualityItems: [
      { title: "Carry case", description: "Case format keeps the kit organized between uses" },
      { title: "Outdoor theme", description: "Leisure positioning makes the gift feel personal" },
      { title: "Compact set", description: "Small kit is easy to store, pack, and transport" },
      { title: "Reward value", description: "More distinctive than common office giveaways" },
    ],
  },
  {
    kind: "gift",
    title: "Globe",
    productName: "Marco Magnetic Levitation Globe",
    heroImage: makeOneStopProductImage("gift", "globe"),
    perspectiveImages: makeOneStopPerspectiveImageSet("gift", "globe"),
    price: "$ 68.88",
    optionLabel: "Version:",
    colorSectionTitle: "STYLE DISPLAY",
    colors: [
      { id: "black", label: "Black", swatchColor: "#1e1f20", textColor: "#ffffff" },
      { id: "blue", label: "Blue", swatchColor: "#34588a", textColor: "#ffffff" },
      { id: "silver", label: "Silver Gray", swatchColor: "#c9c9c9", textColor: "#231815" },
    ],
    productDetails: [
      { label: "Material composition", value: "ABS base and magnetic module" },
      { label: "Care instructions", value: "Wipe Clean" },
      { label: "Origin", value: "Imported" },
      { label: "Closure type", value: "Plug in" },
    ],
    moreDetails: [
      { label: "Version", value: "Desktop display" },
      { label: "Date First Available", value: "30 March 2025" },
      { label: "Manufacturer", value: "Marco Gift" },
      { label: "Marco number", value: "GFT-GLB-001" },
      { label: "Department", value: "Premium gift" },
    ],
    aboutItems: [
      "Premium desk display with a strong technology feeling",
      "Magnetic levitation creates a memorable first impression",
      "Good for VIP customers, partners, and showroom spaces",
      "Compact base keeps the display suitable for desks and counters",
    ],
    sizeOptions: ["Desktop"],
    description:
      "A premium Marco magnetic levitation globe for high-impact partner gifting. It brings a technology-led display moment to desks, counters, and reception areas.",
    qualityItems: [
      { title: "Levitation display", description: "Magnetic module creates a floating globe effect" },
      { title: "Desktop scale", description: "Sized for counters, desks, and reception tables" },
      { title: "Premium signal", description: "A memorable item for VIP and partner gifting" },
      { title: "Stable base", description: "Base design supports a clean display setup" },
    ],
  },
  {
    kind: "gift",
    title: "Notebook",
    productName: "Marco Notebook",
    heroImage: makeOneStopProductImage("gift", "notebook"),
    perspectiveImages: makeOneStopPerspectiveImageSet("gift", "notebook"),
    price: "$ 7.88",
    optionLabel: "Format:",
    colorSectionTitle: "STYLE DISPLAY",
    colors: [
      { id: "black", label: "Black", swatchColor: "#1e1f20", textColor: "#ffffff" },
      { id: "red", label: "Red", swatchColor: "#d9251f", textColor: "#ffffff" },
      { id: "white", label: "White", swatchColor: "#e8e9e8", textColor: "#231815" },
    ],
    productDetails: [
      { label: "Material composition", value: "Paper and PU cover" },
      { label: "Care instructions", value: "Keep Dry" },
      { label: "Origin", value: "Imported" },
      { label: "Closure type", value: "Elastic band" },
    ],
    moreDetails: [
      { label: "Format", value: "A5" },
      { label: "Date First Available", value: "30 March 2025" },
      { label: "Manufacturer", value: "Marco Gift" },
      { label: "Marco number", value: "GFT-NTB-001" },
      { label: "Department", value: "Office gift" },
    ],
    aboutItems: [
      "Useful meeting gift for notes, plans, and showroom visits",
      "Elastic band keeps the notebook closed in bags",
      "Clean cover provides a refined brand presentation",
      "Compact A5 format is easy to carry daily",
    ],
    sizeOptions: ["A5"],
    description:
      "A practical Marco notebook for meetings, training, and daily planning. The compact A5 format makes it an easy item to add to customer welcome kits.",
    qualityItems: [
      { title: "A5 format", description: "Portable size works for meetings and travel" },
      { title: "Elastic closure", description: "Band keeps pages protected inside bags" },
      { title: "Clean cover", description: "Simple cover design keeps the branding professional" },
      { title: "Useful pages", description: "Good everyday writing surface for work notes" },
    ],
  },
  {
    kind: "gift",
    title: "Notes",
    productName: "Marco Sticky Notes",
    heroImage: makeOneStopProductImage("gift", "notes"),
    perspectiveImages: makeOneStopPerspectiveImageSet("gift", "notes"),
    price: "$ 5.88",
    optionLabel: "Set:",
    colorSectionTitle: "STYLE DISPLAY",
    colors: [
      { id: "yellow", label: "Yellow", swatchColor: "#d2b248", textColor: "#231815" },
      { id: "white", label: "White", swatchColor: "#e8e9e8", textColor: "#231815" },
      { id: "red", label: "Red", swatchColor: "#d9251f", textColor: "#ffffff" },
    ],
    productDetails: [
      { label: "Material composition", value: "Paper adhesive notes" },
      { label: "Care instructions", value: "Keep Dry" },
      { label: "Origin", value: "Imported" },
      { label: "Closure type", value: "Pad" },
    ],
    moreDetails: [
      { label: "Set", value: "Memo pad" },
      { label: "Date First Available", value: "30 March 2025" },
      { label: "Manufacturer", value: "Marco Gift" },
      { label: "Marco number", value: "GFT-NTS-001" },
      { label: "Department", value: "Office gift" },
    ],
    aboutItems: [
      "Small desk item for reminders, packaging notes, and quick marks",
      "Easy to include in stationery gift kits",
      "Bright note color stays visible on work surfaces",
      "Useful low-cost gift for events and counter displays",
    ],
    sizeOptions: ["Memo pad"],
    description:
      "A Marco sticky notes pad for everyday office reminders and quick task marks. It is a simple, useful gift that fits naturally into desk stationery kits.",
    qualityItems: [
      { title: "Easy marking", description: "Adhesive notes help flag tasks and pages quickly" },
      { title: "Desk scale", description: "Compact pad fits into office and counter spaces" },
      { title: "Visible color", description: "Bright paper is easy to notice" },
      { title: "Kit friendly", description: "Small format works well in stationery bundles" },
    ],
  },
  {
    kind: "gift",
    title: "Pen",
    productName: "Marco Pen",
    heroImage: makeOneStopProductImage("gift", "pen"),
    perspectiveImages: makeOneStopPerspectiveImageSet("gift", "pen"),
    price: "$ 3.88",
    optionLabel: "Ink:",
    colorSectionTitle: "STYLE DISPLAY",
    colors: [
      { id: "black", label: "Black", swatchColor: "#1e1f20", textColor: "#ffffff" },
      { id: "blue", label: "Blue", swatchColor: "#34588a", textColor: "#ffffff" },
      { id: "red-body", label: "Red", swatchColor: "#d9251f", textColor: "#ffffff" },
    ],
    productDetails: [
      { label: "Material composition", value: "ABS barrel and ballpoint refill" },
      { label: "Care instructions", value: "Keep Capped" },
      { label: "Origin", value: "Imported" },
      { label: "Closure type", value: "Click" },
    ],
    moreDetails: [
      { label: "Ink", value: "Black" },
      { label: "Date First Available", value: "30 March 2025" },
      { label: "Manufacturer", value: "Marco Gift" },
      { label: "Marco number", value: "GFT-PEN-001" },
      { label: "Department", value: "Office gift" },
    ],
    aboutItems: [
      "Everyday branded pen for counters, exhibitions, and office use",
      "Click mechanism is fast and familiar",
      "Slim profile is easy to carry in notebooks and pockets",
      "Simple giveaway item with constant daily visibility",
    ],
    sizeOptions: ["Black ink", "Blue ink"],
    description:
      "A simple Marco ballpoint pen for counters, meetings, and event giveaways. It is an easy everyday gift with high practical value.",
    qualityItems: [
      { title: "Click mechanism", description: "Fast open-close action supports daily writing" },
      { title: "Smooth barrel", description: "Slim shape feels familiar in the hand" },
      { title: "Pocket clip", description: "Clip keeps the pen close to notebooks and pockets" },
      { title: "High visibility", description: "Useful item keeps the brand present every day" },
    ],
  },
  {
    kind: "gift",
    title: "Stapler",
    productName: "Marco Stapler",
    heroImage: makeOneStopProductImage("gift", "stapler"),
    perspectiveImages: makeOneStopPerspectiveImageSet("gift", "stapler"),
    price: "$ 8.88",
    optionLabel: "Type:",
    colorSectionTitle: "STYLE DISPLAY",
    colors: [
      { id: "black", label: "Black", swatchColor: "#1e1f20", textColor: "#ffffff" },
      { id: "red", label: "Red", swatchColor: "#d9251f", textColor: "#ffffff" },
      { id: "gray", label: "Gray", swatchColor: "#c9c9c9", textColor: "#231815" },
    ],
    productDetails: [
      { label: "Material composition", value: "ABS shell and steel rail" },
      { label: "Care instructions", value: "Keep Dry" },
      { label: "Origin", value: "Imported" },
      { label: "Closure type", value: "Press" },
    ],
    moreDetails: [
      { label: "Type", value: "Desktop" },
      { label: "Date First Available", value: "30 March 2025" },
      { label: "Manufacturer", value: "Marco Gift" },
      { label: "Marco number", value: "GFT-STP-001" },
      { label: "Department", value: "Office gift" },
    ],
    aboutItems: [
      "Useful desktop tool for offices, counters, and packing stations",
      "Steel rail supports consistent staple feeding",
      "Compact body saves desk space",
      "Practical branded item for office gift bundles",
    ],
    sizeOptions: ["Desktop"],
    description:
      "A Marco desktop stapler for office desks, counters, and document stations. It is a practical stationery gift that fits naturally into workspaces.",
    qualityItems: [
      { title: "Steel rail", description: "Internal rail supports consistent staple movement" },
      { title: "Compact body", description: "Small footprint keeps the desk tidy" },
      { title: "Press action", description: "Simple action is familiar and easy to use" },
      { title: "Office utility", description: "Daily-use tool fits naturally in stationery kits" },
    ],
  },
];

const equipmentProducts: OneStopProductSource[] = [
  {
    kind: "equipment",
    title: "Forklift",
    productName: "Marco Forklift Solution",
    heroImage: makeOneStopProductImage("equipment", "forklift"),
    perspectiveImages: makeOneStopPerspectiveImageSet("equipment", "forklift"),
    price: "$ 0.00",
    credit: "Consult",
    optionLabel: "Solution:",
    colorSectionTitle: "MODEL DISPLAY",
    colors: [
      { id: "standard", label: "Red", swatchColor: "#d9251f", textColor: "#ffffff" },
      { id: "warehouse", label: "Dark Gray", swatchColor: "#5c5c5c", textColor: "#ffffff" },
      { id: "custom", label: "Light Gray", swatchColor: "#c9c9c9", textColor: "#231815" },
    ],
    productDetails: [
      { label: "Solution type", value: "Warehouse handling equipment" },
      { label: "Service mode", value: "Consulting and sourcing" },
      { label: "Origin", value: "Global supply" },
      { label: "Delivery type", value: "Project based" },
    ],
    moreDetails: [
      { label: "Specification", value: "Consult with Marco team" },
      { label: "Date First Available", value: "30 March 2025" },
      { label: "Provider", value: "Marco Equipment" },
      { label: "Marco number", value: "EQP-FKL-001" },
      { label: "Department", value: "Equipment" },
    ],
    aboutItems: [
      "One stop sourcing support for warehouse handling needs",
      "Useful for distribution, loading, and storage operations",
      "Marco team can help match project requirements",
      "Designed as a consultation-led equipment solution",
    ],
    sizeOptions: ["Consulting"],
    description:
      "A Marco equipment solution page for forklift sourcing and consultation. It frames the category as part of the one stop solution path from selection to delivery discussion.",
    qualityItems: [
      { title: "Requirement matching", description: "Consulting flow helps align model and use case" },
      { title: "Warehouse focus", description: "Equipment supports storage and handling workflows" },
      { title: "Project support", description: "Marco team can coordinate sourcing and next steps" },
      { title: "One stop path", description: "Category connects equipment with parts and service needs" },
    ],
  },
  {
    kind: "equipment",
    title: "DVTestMachine",
    productName: "Marco DV Test Machine",
    heroImage: makeOneStopProductImage("equipment", "dvtestmachine"),
    perspectiveImages: makeOneStopPerspectiveImageSet("equipment", "dvtestmachine"),
    price: "$ 0.00",
    credit: "Consult",
    optionLabel: "Solution:",
    colorSectionTitle: "MODEL DISPLAY",
    colors: [
      { id: "standard", label: "Red", swatchColor: "#d9251f", textColor: "#ffffff" },
      { id: "lab", label: "Dark Gray", swatchColor: "#5c5c5c", textColor: "#ffffff" },
      { id: "custom", label: "Light Gray", swatchColor: "#c9c9c9", textColor: "#231815" },
    ],
    productDetails: [
      { label: "Solution type", value: "Testing equipment" },
      { label: "Service mode", value: "Consulting and sourcing" },
      { label: "Origin", value: "Global supply" },
      { label: "Delivery type", value: "Project based" },
    ],
    moreDetails: [
      { label: "Specification", value: "Consult with Marco team" },
      { label: "Date First Available", value: "30 March 2025" },
      { label: "Provider", value: "Marco Equipment" },
      { label: "Marco number", value: "EQP-DVT-001" },
      { label: "Department", value: "Equipment" },
    ],
    aboutItems: [
      "Test equipment category for workshop and technical teams",
      "Designed for project-specific consultation instead of one-size listings",
      "Supports quality checks and development validation workflows",
      "Marco team can help align requirements before purchase",
    ],
    sizeOptions: ["Consulting"],
    description:
      "A Marco DV test machine solution page for teams that need consultation before selecting equipment. It presents the category in the same detail-page language as the rest of One Stop Solution.",
    qualityItems: [
      { title: "Technical fit", description: "Project consultation helps match test requirements" },
      { title: "Workshop ready", description: "Category supports technical and validation workflows" },
      { title: "Sourcing support", description: "Marco team can coordinate supply options" },
      { title: "Clear next step", description: "Consulting page guides users toward inquiry" },
    ],
  },
  {
    kind: "equipment",
    title: "CuttingPlotter",
    productName: "Marco Cutting Plotter",
    heroImage: makeOneStopProductImage("equipment", "cuttingplotter"),
    perspectiveImages: makeOneStopPerspectiveImageSet("equipment", "cuttingplotter"),
    price: "$ 0.00",
    credit: "Consult",
    optionLabel: "Solution:",
    colorSectionTitle: "MODEL DISPLAY",
    colors: [
      { id: "standard", label: "Blue", swatchColor: "#34588a", textColor: "#ffffff" },
      { id: "shop", label: "Red", swatchColor: "#d9251f", textColor: "#ffffff" },
      { id: "custom", label: "Gray", swatchColor: "#6f6f6f", textColor: "#ffffff" },
    ],
    productDetails: [
      { label: "Solution type", value: "Cutting and plotting equipment" },
      { label: "Service mode", value: "Consulting and sourcing" },
      { label: "Origin", value: "Global supply" },
      { label: "Delivery type", value: "Project based" },
    ],
    moreDetails: [
      { label: "Specification", value: "Consult with Marco team" },
      { label: "Date First Available", value: "30 March 2025" },
      { label: "Provider", value: "Marco Equipment" },
      { label: "Marco number", value: "EQP-CPT-001" },
      { label: "Department", value: "Equipment" },
    ],
    aboutItems: [
      "Cutting plotter category for signage, labeling, and workshop tasks",
      "Useful for operations that need neat cutting and marking workflows",
      "Project consultation helps match material, size, and precision needs",
      "Complements the broader Marco one stop solution offering",
    ],
    sizeOptions: ["Consulting"],
    description:
      "A Marco cutting plotter solution page for users who need equipment consultation. The page keeps the same product-detail structure while positioning the item as a project inquiry.",
    qualityItems: [
      { title: "Workflow fit", description: "Consultation can match plotting needs to operations" },
      { title: "Material planning", description: "Category supports discussions around media and size" },
      { title: "Project sourcing", description: "Marco team can help coordinate equipment options" },
      { title: "One stop support", description: "Equipment sits alongside parts, apparel, and gifts" },
    ],
  },
];

const hatProduct: OneStopProductContent = {
  kind: "apparel",
  slug: "hat",
  title: "Hat",
  categoryLabel: "Apparel",
  productName: "Hat-Cotton",
  heroImage: hatColorOptions[0].image,
  credit: "1000",
  price: "$ 18.88",
  stockLevel: "> 10",
  optionLabel: "Size:",
  colorSectionTitle: "COLOR DISPLAY",
  colorOptions: hatColorOptions,
  productDetails: hatProductDetails,
  moreDetails: hatMoreDetails,
  aboutItems: hatAboutItems,
  sizeOptions: hatSizeOptions,
  promotionItems: hatPromotionItems,
  perspectiveItems: hatPerspectiveItems,
  qualityItems: hatQualityItems,
  description:
    "This is a cotton hat. A simple design that you will never get tired of wearing it, so you can use it for a long time throughout the season. It is unisex, so it can be used by both men and women, so it is recommended as a gift for yourself. Adjustable size can be adjusted.",
};

export const oneStopProducts: OneStopProductContent[] = [
  hatProduct,
  ...apparelProducts.map(buildProduct),
  ...giftProducts.map(buildProduct),
  ...equipmentProducts.map(buildProduct),
];

export const getOneStopProductPath = (product: OneStopProductContent) => `/${product.slug}`;

export const getOneStopProductBySlug = (slug: string) => {
  const normalizedSlug = normalizeOneStopSlug(slug);

  return oneStopProducts.find((product) => product.slug === normalizedSlug) ?? null;
};

export const getOneStopProductByRoute = (kind: OneStopProductKind, slug: string) => {
  const normalizedSlug = normalizeOneStopSlug(slug);

  return oneStopProducts.find((product) => product.kind === kind && product.slug === normalizedSlug) ?? null;
};

export const getOneStopProductByName = (name: string) => {
  const normalizedName = normalizeOneStopSlug(name);

  return oneStopProducts.find((product) => normalizeOneStopSlug(product.title) === normalizedName) ?? null;
};

export const getOneStopProductPathByName = (name: string) => {
  const product = getOneStopProductByName(name);

  return product ? getOneStopProductPath(product) : null;
};

export const getOneStopSolutionPathByName = (name: string) => getOneStopProductPathByName(name);

interface OneStopCategoryIdentity {
  id?: string | number | null;
  frontParentId?: string | number | null;
  backParentId?: string | number | null;
  frontCategoryName?: string | null;
  backCategoryName?: string | null;
  icon?: string | null;
}

const getOneStopCategoryKey = (scope: "title" | "card", category?: OneStopCategoryIdentity | null) => {
  const name = category?.frontCategoryName || category?.backCategoryName || "";
  // 栏目重排不应复用另一张卡片；元组也避免名称中的分隔符造成碰撞。
  return JSON.stringify([
    scope,
    String(category?.frontParentId ?? category?.backParentId ?? ""),
    String(category?.id ?? ""),
    name,
    getOneStopSolutionPathByName(name) ?? category?.icon ?? "",
  ]);
};

export const getOneStopSolutionTitleKey = (category?: OneStopCategoryIdentity | null) =>
  getOneStopCategoryKey("title", category);

export const getOneStopSolutionCardKey = (category?: OneStopCategoryIdentity | null) =>
  getOneStopCategoryKey("card", category);

export const resolveOneStopPublicImage = (image: string, baseURL = "/") => {
  if (!image.startsWith("/image/")) return image;
  // public 资源随应用前缀访问，后端图片和已经解析的路径不重复添加前缀。
  const prefix = baseURL.replace(/^\/+|\/+$/g, "");
  return prefix ? `/${prefix}${image}` : image;
};

export const getEnhancedOneStopProductHeroImageByName = (name: string) => {
  const product = getOneStopProductByName(name);

  return product && product.slug !== "hat" ? product.heroImage : null;
};

export const clampOneStopQuantity = (value: number | string) => {
  const numericValue = typeof value === "string" ? Number.parseInt(value, 10) : value;

  if (!Number.isFinite(numericValue)) {
    return 1;
  }

  return Math.min(9999, Math.max(1, Math.trunc(numericValue)));
};

export const updateOneStopQuantity = (currentValue: number | string, delta: number) =>
  clampOneStopQuantity(clampOneStopQuantity(currentValue) + delta);
